import type { Aabb, CompiledTrack, Platform } from './types'

/** Width of one x-bucket. Engine constant, not balance. */
export const BUCKET_PX = 512

/**
 * Extra x range scanned around a body. Larger than anything moves in one step (a fall is capped
 * at 40 px/tick, the fastest run is under 20), so a push-out never reaches an entry the query
 * skipped and results equal a full scan.
 */
export const NEAR_PAD_PX = 128

/** Entry indices per `BUCKET_PX` column of the track, each list ascending. */
export interface XIndex {
  readonly buckets: readonly (readonly number[])[]
}

/** One index per kind of compiled entry (ARCHITECTURE §2.6 "x-bucket index"). */
export interface TrackIndex {
  readonly solids: XIndex
  readonly platforms: XIndex
  readonly ramps: XIndex
  readonly zones: XIndex
  readonly hazards: XIndex
}

/** Horizontal extent `[x0, x1]` of an entry. */
export type Span = readonly [number, number]

const EMPTY: readonly number[] = []

function bucketOf(x: number, n: number): number {
  return Math.min(n - 1, Math.max(0, Math.floor(x / BUCKET_PX)))
}

/** Buckets cover `[0, max x1]`; anything left of 0 lands in the first, right of the end in the last. */
export function buildXIndex(spans: readonly Span[]): XIndex {
  let n = 1
  for (const [, x1] of spans) n = Math.max(n, Math.floor(x1 / BUCKET_PX) + 1)
  const buckets: number[][] = Array.from({ length: n }, () => [])
  spans.forEach(([x0, x1], k) => {
    for (let b = bucketOf(x0, n); b <= bucketOf(x1, n); b++) buckets[b]!.push(k)
  })
  return { buckets }
}

/** Indices (ascending, unique) of every entry whose span may touch `[x0, x1]`. */
export function nearby(ix: XIndex, x0: number, x1: number): readonly number[] {
  const n = ix.buckets.length
  if (n === 0) return EMPTY
  const b0 = bucketOf(x0, n)
  const b1 = bucketOf(x1, n)
  if (b0 === b1) return ix.buckets[b0]!
  const out: number[] = []
  for (let b = b0; b <= b1; b++) out.push(...ix.buckets[b]!)
  out.sort((a, b) => a - b)
  return out.filter((k, j) => j === 0 || out[j - 1] !== k)
}

/** Entries near a body's horizontal span, padded by `NEAR_PAD_PX`. */
export function nearBody(
  ix: XIndex,
  b: { readonly x: number; readonly w: number },
): readonly number[] {
  return nearby(ix, b.x - NEAR_PAD_PX, b.x + b.w + NEAR_PAD_PX)
}

function boxSpan(a: Aabb): Span {
  return [a.x, a.x + a.w]
}

/** A moving platform's span covers its whole sweep. */
function platformSpan(pl: Platform): Span {
  if (pl.kind !== 'moving') return boxSpan(pl)
  return [Math.min(pl.x, pl.x + pl.dx), Math.max(pl.x, pl.x + pl.dx) + pl.w]
}

/** Attach the x-bucket index to a track's flat arrays. */
export function indexTrack(t: Omit<CompiledTrack, 'index'>): CompiledTrack {
  return {
    ...t,
    index: {
      solids: buildXIndex(t.solids.map(boxSpan)),
      platforms: buildXIndex(t.platforms.map(platformSpan)),
      ramps: buildXIndex(t.ramps.map(boxSpan)),
      zones: buildXIndex(t.zones.map(boxSpan)),
      hazards: buildXIndex(t.hazards.map(boxSpan)),
    },
  }
}
